import { WorkflowExperience } from "../WorkflowExperience";
import { GetWorkflowGraphqlOptions, SpiffCommerceClient } from "../client";
import { graphQlManager } from "../services/server";
import {
    AspectType,
    BundleDesignCreationMessage,
    Bundle as BundleEntity,
    BundleStakeholder,
    BundleStakeholderInput,
    BundleStateTransform,
    CustomerDetailsInput,
    GlobalPropertyConfiguration,
    GlobalPropertyState,
    GlobalPropertyStateColorOptionStorage,
    GlobalPropertyStateFileUploadStorage,
    Order,
    ProductCollectionResource,
    ShareAction,
    StakeholderType,
    StepType,
    Transaction,
} from "../types";
import {
    bundleAddStakeholderMutation,
    bundleAddTransactionMutation,
    bundleAddTransactionsMutation,
    bundleApprovalQuery,
    bundleAssignGlobalPropertyConfiguration,
    bundleAssignProductCollectionMutation,
    bundleRejectionQuery,
    bundleRemoveStakeholderMutation,
    bundleRemoveTransactionMutation,
    bundleRemoveTransactionsMutation,
    bundleUpdateStakeholdersMutation,
    bundleUpdateTransactionOrderMutation,
    finalizeUpdateBundleMutation,
    getBundleStakeholdersQuery,
    updateBundleMutation,
} from "./query";
import {
    amtChannelsForAspect,
    GlobalPropertyHandle,
    GlobalPropertyHandleService,
    OptionGlobalPropertyHandle,
    ColorOptionGlobalPropertyHandle,
    FileUploadGlobalPropertyHandle,
    TextGlobalPropertyHandle,
} from "../GlobalPropertyConfiguration";
import {
    BundleOptions,
    GlobalPropertyStateManager,
    GlobalPropertyStateManagerImpl,
    calculateMandatoryStates,
} from "../GlobalPropertyStateManager";
import type { DesignCreationProgressUpdate, WorkflowManager } from "../WorkflowManager";
import { BundleStateManager } from "./stateManager";
import { persistenceService } from "../services/persistence";
import { ProductCollection } from "../productCollection";
import { createDesigns } from "../services/design";
import type { ModelContainer, ThreeDPreviewService } from "@spiffcommerce/preview";
import { gql } from "@apollo/client/core";
import type { CompleteQuoteMessage } from "@spiffcommerce/theme-bridge";
import { EventEmitter } from "../util/event";
import { BundleEvent, BundleEventMap } from "./event";
import { getAssets } from "../services/asset";
import isEqual from "lodash.isequal";

const addAddressToBundleMutation = gql`
    mutation AddAddressToBundle(
        $bundleId: String!
        $streetAddress: String
        $apartment: String
        $city: String
        $country: String
        $state: String
        $postCode: String
    ) {
        addressAttachToBundle(
            bundleId: $bundleId
            streetAddress: $streetAddress
            apartment: $apartment
            city: $city
            country: $country
            state: $state
            postCode: $postCode
        ) {
            id
        }
    }
`;

const addOrganizationToBundleMutation = gql`
    mutation AddOrganizationToBundle($bundleId: String!, $organizationName: String!) {
        organizationAttachToBundle(bundleId: $bundleId, organizationName: $organizationName) {
            id
        }
    }
`;

const generateQuoteIdMutation = gql`
    mutation GenerateQuoteId($id: String!) {
        bundleGenerateQuoteId(id: $id) {
            id
            quoteId
        }
    }
`;

/**
 * A bundle serves as a container for a set of workflow experience.
 */
export interface Bundle {
    /**
     * @returns The client that this bundle is associated with.
     */
    getClient(): SpiffCommerceClient;

    /**
     * @returns The id of the bundle entity in the Spiff Commerce system.
     */
    getId(): string;

    /**
     * @returns The name of the bundle.
     */
    getName(): string;

    /**
     * @returns True if this bundle is a template, false otherwise.
     */
    getTemplate(): boolean;

    /**
     * @returns The quote id of the bundle, if it has one.
     * This is used to identify the bundle in the Spiff Commerce system for quoting purposes.
     */
    getQuoteId(): string | undefined;

    /**
     * Set the name of the bundle.
     * @param name The new name for the bundle.
     * @returns A promise that resolves when the name has been updated in the Spiff Commerce system.
     */
    setName(name: string): Promise<void>;

    setDispatchDate(dd: string): Promise<void>;

    getDispatchDate(): string | undefined;

    setPurchaseOrder(po: string): Promise<void>;

    getPurchaseOrder(): string | undefined;

    /**
     * @returns Custom metadata associated with this bundle.
     */
    getMetadata(): Map<string, string>;

    /**
     * Set custom metadata associated with this bundle.
     * @param metadata Updated map of metadata items.
     * @returns A promise that resolves when the metadata has been updated in the Spiff Commerce system.
     */
    setMetadata(metadata: Map<string, string>): Promise<void>;

    /**
     * Set the name and metadata for this bundle.
     * @param name The new name for the bundle.
     * @param metadata Updated map of metadata items.
     * @returns A promise that resolves when the name and metadata have been updated in the Spiff Commerce system.
     */
    setNameAndMetadata(name: string, metadata: Map<string, string>): Promise<void>;

    /**
     * Returns true if this bundle is associated with a Product Collection that is linked to a Global Property Configuration
     */
    hasGlobalProperties(): boolean;

    /**
     * Get a handle to the global properties available within this bundle. Setting values
     * via this method will make selections on all bundles that share the property.
     */
    getGlobalProperties(): Promise<GlobalPropertyHandle[]>;

    /**
     * Get the low level state manager for this bundle. Don't use this unless you know what you're doing.
     */
    getGlobalPropertyStateManager(): GlobalPropertyStateManager;

    /**
     * Get the total in subunits for all global properties in this bundle.
     */
    getGlobalPropertyTotalSubunits(): Promise<number>;

    /**
     * Return the total in subunits for this bundle. This is the sum of the total for all individual transactions.
     */
    getTotalSubunits(): number;

    /**
     * Retrieves the product collection associated with this bundle.
     * If the bundle is not associated with a product collection, this method will return undefined.
     */
    getProductCollection(): ProductCollection | undefined;

    /**
     * Add another workflow experience to this bundle.
     * @param experience The workflow experience to add to this bundle.
     */
    addWorkflowExperience(experience: WorkflowExperience): Promise<void>;

    /**
     * Adds multiple workflow experiences to this bundle.
     * @param experiences The workflow experiences to add to this bundle.
     */
    addWorkflowExperiences(experiences: WorkflowExperience[]): Promise<void>;

    /**
     * Remove a workflow experience from this bundle.
     * @param experience The workflow experience to remove from this bundle.
     */
    removeWorkflowExperience(experience: WorkflowExperience): Promise<void>;

    /**
     * Removes multiple workflow experiences from this bundle.
     * @param experiences The workflow experiences to remove from this bundle.
     */
    removeWorkflowExperiences(experiences: WorkflowExperience[]): Promise<void>;

    /**
     * Remove a workflow experience from this bundle by transaction.
     * @param transaction The transaction to remove from this bundle.
     */
    removeWorkflowExperienceByTransaction(transaction: Transaction): Promise<void>;

    /**
     * Removes multiple workflow experiences from this bundle by their transactions.
     * @param transactions The transactions to remove from this bundle.
     */
    removeWorkflowExperiencesByTransactions(transactions: Transaction[]): Promise<void>;

    /**
     * Inserts the given workflow experience into the bundle at the given index.
     * NOTE: If you intend to insert a workflow experience at the end of the bundle, use `addWorkflowExperience` instead as it is more efficient.
     * @param experience The workflow experience to insert.
     * @param index The index to insert the workflow experience at.
     */
    insertWorkflowExperience(experience: WorkflowExperience, index: number): Promise<void>;

    /**
     * Replaces a workflow experience at the given index with the given workflow experience. This essentially removes the old one and inserts the new one.
     * @param index The index to replace the workflow experience at.
     * @param experience The workflow experience to replace the old one with.
     */
    replaceWorkflowExperience(index: number, experience: WorkflowExperience): Promise<void>;

    /**
     * Swaps the workflow experiences at the given indices.
     */
    swapWorkflowExperiences(a: number, b: number): Promise<void>;

    /**
     * Returns all workflow experiences currently added to this bundle.
     */
    getWorkflowExperiences(): WorkflowExperience[];

    /**
     * Sort the list of workflow experiences. The sorting is performed locally, and then the order is updated in the Spiff Commerce system.
     * @returns A promise that resolves when the re-ordering of the workflow experiences has been updated in the Spiff Commerce system.
     * The sorting will occur immediately, but the promise will not resolve until the Spiff Commerce system has been updated.
     */
    sortWorkflowExperiences(sortFunc: (expA: WorkflowExperience, expB: WorkflowExperience) => number): Promise<void>;

    /**
     * @returns The number of workflow experiences in this bundle.
     */
    getWorkflowExperienceCount(): number;

    /**
     * Add a stakeholder to this bundle. If the stakeholder already exists, it will be updated. Write access to the bundle is required.
     * @param customerDetails The details of the customer to add. The emailAddress field is required.
     * @param stakeholderType The type of stakeholder to add. Defaults to Owner.
     */
    addStakeholder(customerDetails: CustomerDetailsInput, stakeholderType?: StakeholderType): Promise<void>;

    /**
     * Removes a stakeholder from this bundle. Write access to the bundle is required.
     * @param emailAddress The email address of the stakeholder to remove.
     */
    removeStakeholder(emailAddress: string): Promise<void>;

    /**
     * Updates all stakeholders associated with this bundle. Write access to the bundle is required.
     * @param stakeholders An array of stakeholders to update. Unknown stakeholders will be added, and absent stakeholders will be removed.
     */
    updateStakeholders(stakeholders: BundleStakeholderInput[]): Promise<void>;

    /**
     * Retrieves all stakeholders associated with this bundle.
     */
    getAllStakeholders(): Promise<BundleStakeholder[]>;

    /**
     * Retrieves the current stakeholder for this bundle. This is the stakeholder that has write access to the bundle.
     * If no stakeholder has write access, this will return undefined.
     * @returns A promise that resolves with the current stakeholder, or undefined if no stakeholder has write access.
     */
    getCurrentStakeholder(): Promise<BundleStakeholder | undefined>;

    /**
     * Finalizes all experiences within the bundle. This will return a promise that resolves when all experiences have been finalized.
     * The promise resolves with a list of messages that indicate the status of each experience including
     * helpful details like what product to add to cart on supported e-commerce platforms.
     * @param onProgressUpdate A callback that will be called when the progress of the finalization changes.
     * @param createPreviewImage A callback that will be called when a preview image is required. This is used to generate a preview image for the design.
     * If this callback is not provided, no preview image will be generated.
     * @returns {BundleDesignCreationMessage} An object containing an array of design messages, along with the Bundle's Id and the event type.
     */
    finish(
        onProgressUpdate?: DesignCreationProgressUpdate,
        createPreviewImage?: (
            workflowExperience: WorkflowExperience,
            shouldRender3D?: boolean,
        ) => Promise<string | undefined>,
    ): Promise<BundleDesignCreationMessage>;

    /**
     * A convenience function allowing you to place an order with all the items in the bundle.
     */
    placeOrder(): Promise<Order>;

    /**
     * Add an event listener to this bundle.
     * @param event The event to listen for. Currently only "conditional-global-properties-changed" is supported.
     * @param listener The listener to call when the event occurs.
     */
    addEventListener<K extends keyof BundleEventMap>(event: K, listener: (event: BundleEvent<K>) => void): void;

    /**
     * Remove a previously added event listener from this bundle.
     */
    removeEventListener<K extends keyof BundleEventMap>(event: K, listener: (event: BundleEvent<K>) => void): void;

    /**
     * Retrieves the current preview service for this bundle, if one exists.
     * If a preview service was specified when calling client.getExistingBundle, this will return that service.
     * Call `setPreviewService` to update the preview service for this bundle.
     */
    getPreviewService(): ThreeDPreviewService | undefined;

    /**
     * Set the preview service for this bundle. This will update the preview service for all workflow experiences in this bundle, and will
     * also automatically inject the preview service into any new workflow experiences that are added to this bundle.
     * If this method is called with no arguments or undefined, the preview service will be removed from this bundle.
     * @param previewService The preview service to use for this bundle.
     * @returns A promise that resolves when the preview service has been updated for all workflow experiences in this bundle.
     */
    setPreviewService(previewService?: ThreeDPreviewService): Promise<void>;

    /**
     * Returns a promise that resolves when the bundle has been initialized.
     * This will resolve immediately if the bundle has no experiences, or if the bundle has already been initialized.
     */
    getInitializationPromise(): Promise<void>;

    /**
     * Sets the position, rotation, and scale of a named transform for a workflow experience within this bundle.
     * @param workflowExperience The workflow experience to update.
     * @param transformName The name of the transform.
     * @param transform An object containing `position`, `rotation`, and `scale` properties. Each property is an object with `x`, `y`, and `z` properties.
     */
    updateWorkflowExperienceTransform(
        workflowExperience: WorkflowExperience,
        transformName: string,
        transform: BundleStateTransform,
    ): Promise<void>;

    /**
     * Sets a transform of a workflow experience within this bundle to be the active one.
     * @param workflowExperience The workflow experience to update.
     * @param transform The name of the transform to make the active transform.
     */
    activateWorkflowExperienceTransform(workflowExperience: WorkflowExperience, transformName: string): Promise<void>;

    /**
     * @returns The raw Global Property Configuration that is in use, otherwise `undefined`.
     */
    getGlobalPropertyConfiguration(): GlobalPropertyConfiguration | undefined;

    setBundleOptions(bundleOptions?: BundleOptions);

    /**
     * Assigns a Product Collection to the Bundle. This will only succeed if the Bundle does not already have a Product Collection assigned to it.
     * @param productCollectionId The ID of the Product Collection to assign to this Bundle.
     */
    assignProductCollection(productCollectionId: string): Promise<void>;

    getShareActions(): undefined | ShareAction[];

    getWorkflowViewerLink(): string;

    getWorkflowViewerAmendLink(): string;

    approve(note?: string, stakeholderId?: string): Promise<void>;

    reject(note?: string, stakeholderId?: string): Promise<void>;

    attachAddress(
        streetAddress?: string,
        apartment?: string,
        city?: string,
        country?: string,
        state?: string,
        postCode?: string,
    ): Promise<void>;

    attachOrganization(name: string): Promise<void>;

    generateQuoteId(): Promise<string>;

    getQuoteCompleteMessage(): CompleteQuoteMessage;

    applyGlobalPropertyState(state: GlobalPropertyState): Promise<void>;

    getIntegrationProductIds(): undefined | string[];

    addIntegrationProductId(id: string): Promise<void>;

    removeIntegrationProductIds(ids: string[]): Promise<void>;
}

export class BundleImpl implements Bundle {
    readonly client: SpiffCommerceClient;

    private readonly id: string;
    private name: string;
    private dispatchDate?: string;
    private purchaseOrder?: string;
    private quoteId?: string;
    private metadata: ReadonlyMap<string, string>;
    private completed: boolean;
    private template: boolean;
    private productCollection?: ProductCollectionResource;

    private readonly bundleStateManager: BundleStateManager;
    private readonly globalPropertyStateManager: GlobalPropertyStateManager;
    private readonly globalPropertyHandleService: GlobalPropertyHandleService;
    private readonly initializationPromise: Promise<any>;
    private readonly ownerId?: string;
    private readonly shareActions?: ShareAction[];
    private readonly workflowViewerLink: string;
    private readonly workflowViewerAmendLink: string;

    private globalConfigurationPropertyId?: string;

    private workflowExperiences: WorkflowExperience[] = [];
    private previewService?: ThreeDPreviewService;

    private eventEmitter = new EventEmitter<BundleEventMap>();

    private updatesLocked = false;

    constructor(
        client: SpiffCommerceClient,
        entity: BundleEntity,
        previewService?: ThreeDPreviewService,
        graphqlOptions?: GetWorkflowGraphqlOptions,
        ownerId?: string,
        bundleOptions?: BundleOptions,
        transactionLoadProgressCallback?: (loaded: number, total: number) => void,
    ) {
        this.client = client;
        this.id = entity.id!;
        this.name = entity.name || "";
        this.dispatchDate = entity.dispatchDate;
        this.template = entity.template || false;
        this.quoteId = entity.quoteId;
        this.shareActions = entity.bundleShareActions;
        this.workflowViewerLink = entity.workflowViewerLink;
        this.workflowViewerAmendLink = entity.workflowViewerAmendLink;
        this.ownerId = ownerId;
        this.metadata = new Map(entity.metadata?.map((m) => [m.key, m.value]) || []);
        this.completed = entity.completed ?? false;
        this.productCollection = entity.productCollection;
        this.bundleStateManager = new BundleStateManager(entity.bundleStateData);
        const defaultGlobalPropertyConfiguration = this.productCollection?.globalPropertyConfiguration;
        this.globalConfigurationPropertyId = defaultGlobalPropertyConfiguration?.id;
        this.globalPropertyStateManager = new GlobalPropertyStateManagerImpl(
            this.id,
            this.ownerId,
            defaultGlobalPropertyConfiguration,
            this.onGlobalPropertiesChanged.bind(this),
            bundleOptions,
        );
        this.globalPropertyHandleService = new GlobalPropertyHandleService(this);
        this.setPreviewService(previewService);

        const defaultVariantsPromise = new Promise<void[]>((resolve, reject) => {
            if (bundleOptions?.readonly) {
                resolve([]);
                return;
            }
            console.log(`Setting default global variants.`);
            const state = this.globalPropertyStateManager.getGlobalPropertyState();
            if (!state) {
                console.log(`Attempted to set global default variant before state was initialized.`);
                resolve([]);
            } else {
                this.globalPropertyHandleService.getHandles().then((handles) => {
                    const promises = handles.map((handle) => {
                        if (handle.getType() === AspectType.ColorOption || handle.getType() === AspectType.Option) {
                            const h = handle as OptionGlobalPropertyHandle;
                            const aspect = state.aspects.find((a) => a.name === h.getName());
                            if (!aspect) {
                                // Only init aspects that aren't already set.
                                return h.initDefaultVariant();
                            }
                        }
                    });
                    Promise.all(promises).then(resolve).catch(reject);
                });
            }
        });

        this.initializationPromise = Promise.all([
            this.loadExistingWorkflowExperiences(
                entity.transactionIds || [],
                {
                    ...graphqlOptions,
                    bundleOwnerId: ownerId,
                },
                transactionLoadProgressCallback,
            ),
            this.globalPropertyStateManager.getInitializationPromise(),
        ]).then(() => defaultVariantsPromise);
        this.initializationPromise.catch((e) => {
            console.error(`Bundle initialization failed: ${e}`);
        });
        this.initializationPromise.then(() => {
            this.getGlobalProperties().then((handles) => this.checkMandatoryHandlesChanged(undefined, handles));
        });
    }

    getIntegrationProductIds(): undefined | string[] {
        return this.bundleStateManager.getState()?.integrationProductIds;
    }

    async addIntegrationProductId(id: string): Promise<void> {
        const state = this.bundleStateManager.getState();
        if (state) {
            if (!state.integrationProductIds) {
                state.integrationProductIds = [];
            } else if (state.integrationProductIds.some((it) => it === id)) {
                // No need to send a network request or update state.
                // Still fire event to maintain existing behaviour
                this.fireEvent("bundle-integration-products-added", {
                    integrationProductIds: state.integrationProductIds,
                });
                return;
            }
            state.integrationProductIds = [...state.integrationProductIds, id];
            const stateString = JSON.stringify(state);
            this.bundleStateManager.setState(stateString);
            this.fireEvent("bundle-integration-products-added", {
                integrationProductIds: state.integrationProductIds,
            });
            await graphQlManager.getShadowGraphqlClient().mutate({
                mutation: updateBundleMutation,
                variables: {
                    id: this.id,
                    bundleStateData: stateString,
                },
                context: {
                    bundleOwnerId: this.ownerId,
                },
            });
        }
    }

    async removeIntegrationProductIds(ids: string[]) {
        const state = this.bundleStateManager.getState();
        if (state) {
            const idsToRemove = ids.filter((id) => (state.integrationProductIds ?? []).includes(id));
            if (idsToRemove.length === 0) {
                return;
            }
            state.integrationProductIds = (state.integrationProductIds ?? []).filter((it) => !idsToRemove.includes(it));
            const stateString = JSON.stringify(state);
            this.bundleStateManager.setState(stateString);
            this.fireEvent("bundle-integration-products-removed", {
                integrationProductIds: idsToRemove,
            });
            await graphQlManager.getShadowGraphqlClient().mutate({
                mutation: updateBundleMutation,
                variables: {
                    id: this.id,
                    bundleStateData: stateString,
                },
                context: {
                    bundleOwnerId: this.ownerId,
                },
            });
        }
    }

    getTemplate(): boolean {
        return this.template;
    }

    async generateQuoteId(): Promise<string> {
        const result = await graphQlManager.getShadowGraphqlClient().mutate<{ bundleGenerateQuoteId: BundleEntity }>({
            mutation: generateQuoteIdMutation,
            variables: {
                id: this.id,
            },
            context: {
                bundleOwnerId: this.ownerId,
            },
        });
        const quoteId = result.data?.bundleGenerateQuoteId?.quoteId;
        this.quoteId = quoteId;
        return quoteId || "";
    }

    async attachAddress(
        streetAddress?: string,
        apartment?: string,
        city?: string,
        country?: string,
        state?: string,
        postCode?: string,
    ): Promise<void> {
        await graphQlManager.getShadowGraphqlClient().mutate({
            mutation: addAddressToBundleMutation,
            variables: {
                bundleId: this.id,
                streetAddress: streetAddress || undefined,
                apartment: apartment || undefined,
                city: city || undefined,
                country: country || undefined,
                state: state || undefined,
                postCode: postCode || undefined,
            },
            context: {
                bundleOwnerId: this.ownerId,
            },
        });
    }

    async attachOrganization(name: string): Promise<void> {
        await graphQlManager.getShadowGraphqlClient().mutate({
            mutation: addOrganizationToBundleMutation,
            variables: {
                bundleId: this.id,
                organizationName: name,
            },
            context: {
                bundleOwnerId: this.ownerId,
            },
        });
    }

    setBundleOptions(bundleOptions?: BundleOptions) {
        this.globalPropertyStateManager.setBundleOptions(bundleOptions);
    }

    getInitializationPromise(): Promise<void> {
        return this.initializationPromise;
    }

    addEventListener<K extends keyof BundleEventMap>(event: K, listener: (event: BundleEvent<K>) => void): void {
        this.eventEmitter.on(event, listener);
    }

    removeEventListener<K extends keyof BundleEventMap>(event: K, listener: (event: BundleEvent<K>) => void): void {
        this.eventEmitter.off(event, listener);
    }

    getClient() {
        return this.client;
    }

    getId() {
        return this.id;
    }

    getName(): string {
        return this.name;
    }

    getQuoteId(): string | undefined {
        return this.quoteId;
    }

    setName(name: string): Promise<void> {
        this.name = name;
        return this.updateBundle();
    }

    getDispatchDate(): string | undefined {
        return this.dispatchDate;
    }

    setDispatchDate(dd: string): Promise<void> {
        this.dispatchDate = dd;
        return this.updateBundle();
    }

    getPurchaseOrder(): string | undefined {
        return this.purchaseOrder;
    }

    setPurchaseOrder(po: string): Promise<void> {
        this.purchaseOrder = po;
        return this.updateBundle();
    }

    getMetadata(): Map<string, string> {
        return new Map(this.metadata);
    }

    setMetadata(metadata: Map<string, string>): Promise<void> {
        this.metadata = new Map(metadata);
        return this.updateBundle();
    }

    setNameAndMetadata(name: string, metadata: Map<string, string>): Promise<void> {
        this.name = name;
        this.metadata = new Map(metadata);
        return this.updateBundle();
    }

    hasGlobalProperties(): boolean {
        // For now we just check if this bundle is linked to a product collection that has global properties.
        // In the future there could be other ways of providing global properties.
        return !!this.productCollection?.globalPropertyConfiguration;
    }

    getGlobalPropertyConfiguration() {
        return this.productCollection?.globalPropertyConfiguration;
    }

    async getGlobalProperties(): Promise<GlobalPropertyHandle[]> {
        return this.globalPropertyHandleService.applyConditionsFromState(
            await this.globalPropertyHandleService.getHandles(),
            this.getGlobalPropertyStateManager().getGlobalPropertyState(),
        );
    }

    async applyGlobalPropertyState(state: GlobalPropertyState): Promise<void> {
        this.updatesLocked = true;
        const handles = await this.globalPropertyHandleService.getHandles();
        try {
            // First we have to ensure that the aspects have been set,
            // because the rest of the logic relies on it.
            for (const aspect of state.aspects) {
                const name = aspect?.name;
                await this.getGlobalPropertyStateManager().setAspect(name, aspect.value);
            }

            // Then we do the general work.
            for (const aspect of state.aspects) {
                const name = aspect?.name;
                const handle = handles.find((h) => h.getName() === name);
                if (handle) {
                    switch (aspect.type) {
                        case AspectType.FileUpload: {
                            const fileHandle = handle as FileUploadGlobalPropertyHandle;
                            const assets = await getAssets([aspect.value]);
                            if (assets.length > 0) {
                                await fileHandle.selectImage(assets[0]);
                            }
                            const colors = (aspect.storage as GlobalPropertyStateFileUploadStorage | undefined)?.colors;
                            if (colors) {
                                const colorMap = Object.fromEntries(
                                    colors.map((it) => [
                                        it.key,
                                        { browserValue: it.browserValue, pmsValue: it.pmsValue },
                                    ]) ?? [],
                                );
                                await fileHandle.changeColors(colorMap);
                            }
                            break;
                        }
                        case AspectType.ColorOption: {
                            const colorHandle = handle as ColorOptionGlobalPropertyHandle;
                            const variants = colorHandle.getAllVariants();
                            const variant = variants.find((v) => v.getId() === aspect.value);
                            const storage = aspect.storage
                                ? (aspect.storage as GlobalPropertyStateColorOptionStorage)
                                : undefined;
                            if (variant) {
                                await colorHandle.selectVariant(variant);
                            }
                            if (storage?.customColor) {
                                colorHandle.setCustomColor(storage.customColor);
                            }
                            break;
                        }
                        case AspectType.Option: {
                            const optionHandle = handle as OptionGlobalPropertyHandle;
                            const variants = optionHandle.getAllVariants();
                            const variant = variants.find((v) => v.getId() === aspect.value);
                            if (variant) {
                                await optionHandle.selectVariant(variant);
                            }
                            break;
                        }
                        case AspectType.Text: {
                            const textHandle = handle as TextGlobalPropertyHandle;
                            await textHandle.setText(aspect.value);
                            break;
                        }
                    }
                }
            }
        } finally {
            this.updatesLocked = false;
            // Fire off any relevant events
            this.onGlobalPropertiesChanged(undefined, this.globalPropertyStateManager.getGlobalPropertyState()!);
        }
    }

    getGlobalPropertyStateManager(): GlobalPropertyStateManager {
        return this.globalPropertyStateManager;
    }

    async getGlobalPropertyTotalSubunits(): Promise<number> {
        const handles = await this.globalPropertyHandleService.getHandles();
        const options = handles.filter((h) => h.getType() === AspectType.Option) as OptionGlobalPropertyHandle[];
        return options.map((o) => o.getCurrentVariant()?.getPrice() || 0).reduce((partialSum, a) => partialSum + a, 0);
    }

    getTotalSubunits() {
        return this.workflowExperiences.map((we) => we.getTotalPriceSubunits()).reduce((a, b) => a + b, 0);
    }

    getProductCollection(): ProductCollection | undefined {
        return this.productCollection ? new ProductCollection(this.productCollection) : undefined;
    }

    private async initializeAdditionalRequiredColorChannels(experience: WorkflowExperience) {
        // If the experience corresponds to a global illustration we check whether the
        // experience we're adding has more channels than have been initialized.
        for (const step of experience.getStepsByType(StepType.Illustration)) {
            const aspectNames = step.getGlobalPropertyAspects(this.globalConfigurationPropertyId || "");
            for (const aspectName of aspectNames) {
                const matchingAspect = this.getGlobalPropertyConfiguration()?.aspects.find(
                    (a) => a.name === aspectName,
                );
                if (matchingAspect && matchingAspect.type === AspectType.ColorOption) {
                    const asset = step.getCurrentVariant()?.getAssetResource();
                    const channelNumbers = asset?.assetConfiguration?.channelNumbers || [];
                    const defaultColors = asset?.assetConfiguration?.defaultColorVariants || [];
                    const amtChannelsInitialized = amtChannelsForAspect(
                        this.getWorkflowExperiences(),
                        this.globalConfigurationPropertyId || "",
                        aspectName,
                    );
                    const uniqueChannelNumbers = [...new Set(channelNumbers.map((cn) => cn.number))];
                    const amtChannelsInExperience = uniqueChannelNumbers.length;
                    if (amtChannelsInExperience > amtChannelsInitialized) {
                        for (const channelNumber of uniqueChannelNumbers) {
                            const manager = this.getGlobalPropertyStateManager();
                            const dc = defaultColors.find((dc) => dc.channelNumber === channelNumber);
                            if (dc) {
                                await manager.setAspect(aspectName, dc.variant.id || "", undefined, dc.channelNumber);
                            }
                        }
                    }
                }
            }
        }
    }

    async addWorkflowExperience(experience: WorkflowExperience) {
        await this.initializeAdditionalRequiredColorChannels(experience);

        // Append the experience to the bundle.
        experience.setBundle(this);
        await this.appendWorkflowExperience(experience);
        // If a preview is attached to the bundle inject it into that.
        await this.injectExperienceIntoPreviewService(experience);
        // Ensure the experience is loaded to prevent timing issues with global property application.
        await experience.getWorkflowManager().getInitializationPromise();

        // Apply global state if required.
        const globalProperties = await this.getGlobalProperties();
        for (const p of globalProperties) {
            await p.applyGlobalState([experience]);
        }

        this.fireEvent("workflow-experience-added", {
            workflowExperiences: [experience],
        });
    }

    async addWorkflowExperiences(experiences: WorkflowExperience[]) {
        for (const experience of experiences) {
            await this.initializeAdditionalRequiredColorChannels(experience);
        }

        const transactionIds = experiences.map((experience) => experience.getTransaction().id);
        const existing = transactionIds.map((transactionId) =>
            this.workflowExperiences.find((exp) => exp.getTransaction().id === transactionId),
        );
        if (existing.some((e) => !!e))
            throw new Error(
                "Unable to add transaction to bundle - Already Exists: " +
                    existing
                        .filter((e) => !!e)
                        .map((e) => e?.getTransaction().id)
                        .join(", "),
            );
        // Use the owner ID from the persistence service if it exists. Otherwise the client will fall back to other methods.
        const ownerMap = persistenceService.getMap("transactionOwnerIds") || new Map();
        const existingOwnerIds = transactionIds.map((transactionId) => ownerMap.get(transactionId));
        await graphQlManager
            .getShadowGraphqlClient()
            .mutate({
                mutation: bundleAddTransactionsMutation,
                variables: {
                    id: this.id,
                    transactionIds: transactionIds,
                    transactionOwnerIds: existingOwnerIds,
                },
                context: {
                    bundleOwnerId: this.ownerId,
                },
            })
            .catch((e) => {
                console.error(e);
            });
        this.workflowExperiences.push(...experiences);

        const initializeExperience = async () => {
            for (const experience of experiences) {
                experience.setBundle(this);
                await this.injectExperienceIntoPreviewService(experience);
            }
            await Promise.all(experiences.map((exp) => exp.getWorkflowManager().getInitializationPromise()));
            const globalProperties = await this.getGlobalProperties();
            for (const p of globalProperties) {
                await p.applyGlobalState(experiences);
            }
        };

        await Promise.all([this.updateTransactionOrder(), initializeExperience()]);
        // Check changes after adding all (experiences rely on checking each other)
        this.workflowExperiences.forEach((experience) => experience.checkForPriceBreakChanges());

        this.fireEvent("workflow-experience-added", {
            workflowExperiences: this.workflowExperiences,
        });
    }

    private async appendWorkflowExperience(experience: WorkflowExperience, updateArray = true) {
        const transactionId = experience.getTransaction().id;
        const existing = this.workflowExperiences.find((exp) => exp.getTransaction().id === transactionId);
        if (existing) throw new Error("Unable to add transaction to bundle - Already Exists!");
        // Use the owner ID from the persistence service if it exists. Otherwise the client will fall back to other methods.
        const ownerMap = persistenceService.getMap("transactionOwnerIds") || new Map();
        const existingOwnerId = ownerMap.get(transactionId);
        await graphQlManager.getShadowGraphqlClient().mutate({
            mutation: bundleAddTransactionMutation,
            variables: {
                id: this.id,
                transactionId: transactionId,
            },
            context: {
                bundleOwnerId: this.ownerId,
                transactionOwnerId: existingOwnerId,
            },
        });
        if (updateArray) {
            this.workflowExperiences.push(experience);
            this.workflowExperiences.forEach((experience) => experience.checkForPriceBreakChanges());
        }
    }

    async removeWorkflowExperience(experience: WorkflowExperience) {
        const index = this.workflowExperiences.indexOf(experience);
        await this.removeTransaction(index, this.workflowExperiences[index].getTransaction());
        this.fireEvent("workflow-experience-added", {
            workflowExperiences: [experience],
        });
    }

    async removeWorkflowExperiences(experiences: WorkflowExperience[]) {
        await this.removeTransactions(experiences.map((exp) => exp.getTransaction()));
    }

    async removeWorkflowExperienceByTransaction(transaction: Transaction) {
        const index = this.workflowExperiences.findIndex((we) => we.getTransaction().id === transaction.id);
        await this.removeTransaction(index, transaction);
    }

    async removeWorkflowExperiencesByTransactions(transactions: Transaction[]) {
        await this.removeTransactions(transactions);
    }

    private async removeTransaction(index: number, transaction: Transaction, updateArray = true) {
        if (index > -1) {
            const removed = this.workflowExperiences.find((we) => we.getTransaction().id === transaction.id)!;
            removed.setBundle(undefined);
            if (this.previewService) {
                removed.getWorkflowManager().ejectFromPreviewService();
            }
            this.bundleStateManager.removeStateForTransaction(transaction.id);
            await graphQlManager.getShadowGraphqlClient().mutate({
                mutation: bundleRemoveTransactionMutation,
                variables: {
                    id: this.id,
                    transactionId: transaction.id,
                    name: this.name,
                    metadata: Array.from(this.metadata.entries()).map(([key, value]) => ({ key, value })),
                    bundleStateData: this.bundleStateManager.getSerializedState(),
                },
                context: {
                    bundleOwnerId: this.ownerId,
                },
            });
            if (updateArray) {
                const removedExp = this.workflowExperiences.splice(index, 1);
                this.workflowExperiences.forEach((experience) => experience.checkForPriceBreakChanges());
                removed.checkForPriceBreakChanges();
                await this.updateTransactionOrder();
                this.fireEvent("workflow-experience-removed", {
                    workflowExperiences: removedExp,
                });
            }
        } else {
            throw new Error("Unable to remove workflow experience from bundle - Not Found! - " + transaction.id);
        }
    }

    private async removeTransactions(transactions: Transaction[]) {
        const indices = transactions.map((transaction) =>
            this.workflowExperiences.findIndex((we) => we.getTransaction().id === transaction.id),
        );
        if (indices.some((index) => index === -1)) {
            throw new Error(
                "Unable to remove workflow experience from bundle - The following aren't included in the bundle: " +
                    transactions
                        .filter((_, i) => indices[i] === -1)
                        .map((t) => t.id)
                        .join(", "),
            );
        }
        const removedExperiences = transactions.map(
            (t) =>
                this.workflowExperiences.splice(
                    this.workflowExperiences.findIndex((we) => we.getTransaction().id === t.id),
                    1,
                )[0],
        );
        removedExperiences.forEach((experience) => experience.setBundle(undefined));
        [...this.workflowExperiences, ...removedExperiences].forEach((experience) =>
            experience.checkForPriceBreakChanges(),
        );
        if (this.previewService) {
            removedExperiences.forEach((experience) => experience.getWorkflowManager().ejectFromPreviewService());
        }
        transactions.forEach((transaction) => this.bundleStateManager.removeStateForTransaction(transaction.id));
        await graphQlManager.getShadowGraphqlClient().mutate({
            mutation: bundleRemoveTransactionsMutation,
            variables: {
                id: this.id,
                transactionIds: transactions.map((t) => t.id),
                name: this.name,
                metadata: Array.from(this.metadata.entries()).map(([key, value]) => ({ key, value })),
                bundleStateData: this.bundleStateManager.getSerializedState(),
            },
            context: {
                bundleOwnerId: this.ownerId,
            },
        });
        this.fireEvent("workflow-experience-removed", {
            workflowExperiences: removedExperiences,
        });
        await this.updateTransactionOrder();
    }

    async insertWorkflowExperience(experience: WorkflowExperience, index: number): Promise<void> {
        await this.appendWorkflowExperience(experience, false);
        this.workflowExperiences.splice(index, 0, experience);
        await Promise.all([
            this.updateTransactionOrder(),
            (async () => {
                await this.injectExperienceIntoPreviewService(experience);
                const globalProperties = await this.getGlobalProperties();
                await Promise.all(globalProperties.map((p) => p.applyGlobalState([experience])));
            })(),
        ]);
    }

    async replaceWorkflowExperience(index: number, experience: WorkflowExperience): Promise<void> {
        if (index < 0 || index >= this.workflowExperiences.length) {
            throw new Error("Unable to replace workflow experience in bundle - Index out of range!");
        }
        const oldExperience = this.workflowExperiences[index];
        if (oldExperience.getTransaction().id === experience.getTransaction().id) {
            throw new Error("Unable to replace workflow experience in bundle - Same transaction!");
        }
        await Promise.all([
            this.removeTransaction(index, oldExperience.getTransaction(), false),
            this.appendWorkflowExperience(experience, false),
        ]);
        this.workflowExperiences[index] = experience;
        await Promise.all([
            this.updateTransactionOrder(),
            (async () => {
                await this.injectExperienceIntoPreviewService(experience);
                const globalProperties = await this.getGlobalProperties();
                await Promise.all(globalProperties.map((p) => p.applyGlobalState([experience])));
            })(),
        ]);
    }

    async swapWorkflowExperiences(a: number, b: number): Promise<void> {
        if (a < 0 || a >= this.workflowExperiences.length || b < 0 || b >= this.workflowExperiences.length) {
            throw new Error("Unable to swap workflow experiences in bundle - Index out of range!");
        }
        if (a === b) return;
        const temp = this.workflowExperiences[a];
        this.workflowExperiences[a] = this.workflowExperiences[b];
        this.workflowExperiences[b] = temp;
        await this.updateTransactionOrder();
    }

    getWorkflowExperiences() {
        return [...this.workflowExperiences];
    }

    async sortWorkflowExperiences(
        sortFunc: (expA: WorkflowExperience, expB: WorkflowExperience) => number,
    ): Promise<void> {
        this.workflowExperiences = this.workflowExperiences.sort(sortFunc);
        await this.updateTransactionOrder();
    }

    getWorkflowExperienceCount(): number {
        return this.workflowExperiences.length;
    }

    async addStakeholder(customerDetails: CustomerDetailsInput, stakeholderType?: StakeholderType): Promise<void> {
        const response = await graphQlManager.getShadowGraphqlClient().mutate<{ bundleAddStakeholder: BundleEntity }>({
            mutation: bundleAddStakeholderMutation,
            variables: {
                id: this.id,
                details: customerDetails,
                type: stakeholderType || StakeholderType.Owner,
            },
            context: {
                bundleOwnerId: this.ownerId,
            },
        });
        if (!response.data?.bundleAddStakeholder) throw new Error("Bundle not found!");
        const stakeholders = response.data.bundleAddStakeholder.bundleStakeholders || [];
        this.storeStakeholderCustomers(stakeholders);
    }

    async removeStakeholder(emailAddress: string): Promise<void> {
        const response = await graphQlManager
            .getShadowGraphqlClient()
            .mutate<{ bundleRemoveStakeholder: BundleEntity }>({
                mutation: bundleRemoveStakeholderMutation,
                variables: {
                    id: this.id,
                    emailAddress,
                },
                context: {
                    bundleOwnerId: this.ownerId,
                },
            });
        if (!response.data?.bundleRemoveStakeholder) throw new Error("Bundle not found!");
    }

    async updateStakeholders(stakeholders: BundleStakeholderInput[]): Promise<void> {
        const response = await graphQlManager
            .getShadowGraphqlClient()
            .mutate<{ bundleUpdateStakeholders: BundleEntity }>({
                mutation: bundleUpdateStakeholdersMutation,
                variables: {
                    id: this.id,
                    input: stakeholders,
                },
                context: {
                    bundleOwnerId: this.ownerId,
                },
            });
        if (!response.data?.bundleUpdateStakeholders) throw new Error("Bundle not found!");
        const updatedStakeholders = response.data.bundleUpdateStakeholders.bundleStakeholders || [];
        this.storeStakeholderCustomers(updatedStakeholders);
    }

    async getAllStakeholders(): Promise<BundleStakeholder[]> {
        const response = await graphQlManager.getShadowGraphqlClient().query<{ bundles: BundleEntity[] }>({
            query: getBundleStakeholdersQuery,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                id: this.id,
            },
            context: {
                bundleOwnerId: this.ownerId,
            },
        });
        if (!response.data.bundles || response.data.bundles.length === 0) throw new Error("Bundle not found!");
        const stakeholders = response.data.bundles[0].bundleStakeholders || [];
        this.storeStakeholderCustomers(stakeholders);
        return stakeholders;
    }

    async getCurrentStakeholder(): Promise<BundleStakeholder | undefined> {
        const response = await graphQlManager.getShadowGraphqlClient().query<{ bundles: BundleEntity[] }>({
            query: getBundleStakeholdersQuery,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                id: this.id,
            },
            context: {
                bundleOwnerId: this.ownerId,
            },
        });
        if (!response.data.bundles || response.data.bundles.length === 0) throw new Error("Bundle not found!");
        const bundle = response.data.bundles[0];
        return bundle.currentBundleStakeholder;
    }

    private storeStakeholderCustomers(stakeholders: BundleStakeholder[]) {
        stakeholders.forEach((stakeholder) => {
            if (stakeholder.customer) {
                this.client.storeCustomer(stakeholder.customer);
            }
        });
    }

    async finish(
        onProgressUpdate?: DesignCreationProgressUpdate,
        createPreviewImage?: (
            workflowExperience: WorkflowExperience,
            shouldRender3D?: boolean,
        ) => Promise<string | undefined>,
    ): Promise<BundleDesignCreationMessage> {
        // Flush any changes beforehand
        if (this.completed) {
            await graphQlManager.getShadowGraphqlClient().mutate({
                mutation: finalizeUpdateBundleMutation,
                variables: {
                    bundleId: this.id,
                },
            });
        }
        // Compute the results
        const results = await createDesigns(
            this.workflowExperiences.map((exp) => {
                const workflow = exp.getWorkflow();
                if (!workflow) throw new Error("Unable to create designs - Workflow not found!");
                const product = exp.getProduct();
                if (!product) throw new Error("Unable to create designs - Product not found!");
                return {
                    workflowManager: exp.getWorkflowManager(),
                    workflow: workflow,
                    layouts: exp.getWorkflowManager().getLayouts(),
                    getReducerState: () => exp.getCommandContext().getState()!,
                    product: product,
                    transaction: exp.getTransaction(),
                    workflowSelections: exp.getWorkflowManager().getWorkflowSelections(),
                    designName: workflow.name,
                    workflowMetadata: exp.getWorkflowManager().getWorkflowMetadata(),
                };
            }),
            onProgressUpdate ?? (() => {}),
            createPreviewImage
                ? (shouldRender3D?: boolean, transactionId?: string) => {
                      const experience = this.workflowExperiences.find(
                          (exp) => exp.getTransaction().id === transactionId,
                      );
                      if (!experience) {
                          console.warn(
                              "SpiffCommerce - Bundle create design - Unable to find experience for transaction: " +
                                  transactionId,
                          );
                          return Promise.resolve(undefined);
                      }
                      return createPreviewImage(experience, shouldRender3D);
                  }
                : undefined,
        );
        return {
            bundleId: this.id,
            name: this.getName(),
            dispatchDate: this.getDispatchDate() || "",
            purchaseOrder: this.getPurchaseOrder() || "",
            collectionId: this.getProductCollection()?.getId() || "",
            collectionName: this.getProductCollection()?.getName() || "",
            items: results,
            bundleOwnerId: this.ownerId,
        };
    }

    async placeOrder(): Promise<Order> {
        const orderItems = this.workflowExperiences.map((exp) => {
            return {
                transactionId: exp.getTransaction().id,
                amountToOrder: exp.getTransaction().quantity || 1,
            };
        });
        return await this.getClient().placeOrder(orderItems);
    }

    getPreviewService() {
        return this.previewService;
    }

    async setPreviewService(previewService?: ThreeDPreviewService): Promise<void> {
        if (this.workflowExperiences && this.workflowExperiences.length > 0) {
            this.workflowExperiences.forEach((exp) => exp.getWorkflowManager().ejectFromPreviewService());
            if (previewService) {
                // Sequentially inject each experience into the preview service, for performance reasons.
                for (const experience of this.workflowExperiences) {
                    await this.injectExperienceIntoPreviewService(experience);
                }
            }
        }
        if (previewService && previewService.registerModelLoadEventListener) {
            previewService.registerModelLoadEventListener(this.onModelLoadEvent.bind(this));
        }
        if (
            this.previewService &&
            this.previewService !== previewService &&
            this.previewService.unregisterModelLoadEventListener
        ) {
            this.previewService.unregisterModelLoadEventListener(this.onModelLoadEvent.bind(this));
        }
        this.previewService = previewService;
    }

    updateWorkflowExperienceTransform(
        workflowExperience: WorkflowExperience,
        transformName: string,
        transform: BundleStateTransform,
    ): Promise<void> {
        this.bundleStateManager.updateWorkflowExperienceTransform(workflowExperience, transformName, transform);
        return this.updateBundle();
    }

    activateWorkflowExperienceTransform(workflowExperience: WorkflowExperience, transformName: string): Promise<void> {
        this.bundleStateManager.activateWorkflowExperienceTransform(workflowExperience, transformName);
        return this.updateBundle();
    }

    async assignGlobalPropertyConfiguration(globalPropertyConfigurationId: string) {
        await graphQlManager.getShadowGraphqlClient().mutate<{ bundleAssignProductCollection: BundleEntity }>({
            mutation: bundleAssignGlobalPropertyConfiguration,
            variables: {
                bundleId: this.id,
                globalPropertyConfigurationId,
            },
            context: {
                bundleOwnerId: this.ownerId,
            },
        });
        this.globalConfigurationPropertyId = globalPropertyConfigurationId;
    }

    async assignProductCollection(productCollectionId: string) {
        const response = await graphQlManager
            .getShadowGraphqlClient()
            .mutate<{ bundleAssignProductCollection: BundleEntity }>({
                mutation: bundleAssignProductCollectionMutation(
                    this.globalPropertyStateManager.getBundleOptions()?.eagerFetchProducts ?? false,
                ),
                variables: {
                    id: this.id,
                    productCollectionId: productCollectionId ?? "",
                },
                context: {
                    bundleOwnerId: this.ownerId,
                },
            });
        if (response.data?.bundleAssignProductCollection.productCollection) {
            this.productCollection = response.data.bundleAssignProductCollection.productCollection;
            const globalPropertyConfiguration =
                response.data.bundleAssignProductCollection.productCollection.globalPropertyConfiguration;
            this.globalPropertyStateManager.setConfiguration(globalPropertyConfiguration);
            this.globalConfigurationPropertyId = globalPropertyConfiguration?.id;
        }
    }

    private onModelLoadEvent(e: { eventType: "load" | "unload"; modelContainer: ModelContainer }): void {
        if (e.eventType === "unload" || !e.modelContainer) return;
        e.modelContainer.registerMaterialSelectedCallback(() => this.onModelSelectedEvent(e.modelContainer, true));
        e.modelContainer.registerMaterialDeselectedCallback(() => this.onModelSelectedEvent(e.modelContainer, false));
    }

    private onModelSelectedEvent(modelContainer: ModelContainer, selected: boolean): void {
        if (modelContainer && modelContainer.metadata && modelContainer.metadata instanceof Map) {
            if (modelContainer.metadata.has("workflowManager")) {
                const workflowManager: WorkflowManager = modelContainer.metadata.get("workflowManager");
                const transaction = workflowManager.getWorkflowExperience().getTransaction();
                const experience = this.workflowExperiences.find((exp) => exp.getTransaction().id === transaction.id);
                if (experience) {
                    this.fireEvent(selected ? "workflow-experience-hover-enter" : "workflow-experience-hover-exit", {
                        workflowExperience: experience,
                    });
                }
            }
        }
    }

    private async injectExperienceIntoPreviewService(experience: WorkflowExperience) {
        if (this.previewService) {
            const promise = experience.getWorkflowManager().injectIntoPreviewService(this.previewService);
            try {
                this.bundleStateManager.applyStateToWorkflowExperience(experience);
            } catch (e) {
                console.error(`Unable to apply state to workflow experience: ${e}`);
            }
            await promise;
        }
    }

    private async loadExistingWorkflowExperiences(
        transactionIds: string[],
        graphqlOptions?: GetWorkflowGraphqlOptions,
        transactionLoadProgressCallback?: (loaded: number, total: number) => void,
    ): Promise<void> {
        if (transactionIds.length === 0) return;
        const experiences = await this.client.getWorkflowExperiences(
            transactionIds.map((id) => ({
                type: "transaction",
                transactionId: id,
            })),
            graphqlOptions,
            undefined,
            transactionLoadProgressCallback,
        );
        experiences.forEach((wfe) => wfe.setBundle(this));
        experiences.forEach((wfe) => wfe.checkForPriceBreakChanges());
        this.workflowExperiences = experiences;
        if (this.previewService) {
            for (const experience of experiences) {
                await this.injectExperienceIntoPreviewService(experience);
            }
        }
    }

    private async updateBundle() {
        const response = await graphQlManager.getShadowGraphqlClient().mutate<{ bundleUpdate: BundleEntity }>({
            mutation: updateBundleMutation,
            variables: {
                id: this.id,
                name: this.name,
                metadata: Array.from(this.metadata.entries()).map(([key, value]) => ({ key, value })),
                bundleStateData: this.bundleStateManager.getSerializedState(),
                dispatchDate: this.dispatchDate,
                purchaseOrder: this.purchaseOrder,
            },
            context: {
                bundleOwnerId: this.ownerId,
            },
        });
        if (!response.data?.bundleUpdate) throw new Error("Bundle not found!");
    }

    private async updateTransactionOrder() {
        await graphQlManager.getShadowGraphqlClient().mutate({
            mutation: bundleUpdateTransactionOrderMutation,
            variables: {
                id: this.id,
                transactionIds: this.workflowExperiences.map((exp) => exp.getTransaction().id),
            },
            context: {
                bundleOwnerId: this.ownerId,
            },
        });
    }

    private async onGlobalPropertiesChanged(
        previousState: GlobalPropertyState | undefined,
        newState: GlobalPropertyState,
    ) {
        if (this.updatesLocked) {
            return;
        }
        const handles = await this.globalPropertyHandleService.getHandles();
        const previousHandles = this.globalPropertyHandleService.applyConditionsFromState(handles, previousState);
        const newHandles = this.globalPropertyHandleService.applyConditionsFromState(handles, newState);

        // Check changes and fire events
        this.checkConditionalHandlesChanged(previousHandles, newHandles);
        this.checkMandatoryHandlesChanged(previousState, newHandles);

        // Aspects that are no longer active need to be cleared from the
        // state (particularly to deactivate aspects that are transitively dependent).
        const stateManager = this.getGlobalPropertyStateManager();
        let oldAspects: string[] = [];
        for (const handle of handles) {
            if (!newHandles.find((newHandle) => newHandle.getName() == handle.getName())) {
                oldAspects = [...oldAspects, handle.getName()];
            }
        }
        await stateManager.clearAspects(oldAspects);

        // Apply default variants on new handles
        const promises = newHandles.map((handle) => {
            if (handle.getType() === AspectType.ColorOption || handle.getType() === AspectType.Option) {
                const h = handle as OptionGlobalPropertyHandle;
                const aspect = newState.aspects.find((a) => a.name === h.getName());
                if (!aspect) {
                    // Only init aspects that aren't already set.
                    return h.initDefaultVariant();
                }
            }
        });
        await Promise.all(promises);

        // Ensure new handles' associated workflow experiences have up-to-date global property state
        await Promise.all(newHandles.map((handle) => handle.applyGlobalState()));

        const resultingState = this.globalPropertyStateManager.getGlobalPropertyState()!;
        const nextHandles = this.globalPropertyHandleService.applyConditionsFromState(handles, resultingState);
        if (!isEqual(newHandles.sort(), nextHandles.sort())) {
            // Recurse to remove transitively conditional steps.
            this.checkConditionalHandlesChanged(newHandles, nextHandles);
            this.checkMandatoryHandlesChanged(resultingState, nextHandles);
            await this.onGlobalPropertiesChanged(newState, resultingState);
        }
    }

    private checkConditionalHandlesChanged(
        previousHandles: GlobalPropertyHandle[],
        newHandles: GlobalPropertyHandle[],
    ) {
        const checkHandlesChanged = () => {
            if (previousHandles.length !== newHandles.length) return true;
            for (let i = 0; i < previousHandles.length; i++) {
                if (previousHandles[i].getName() !== newHandles[i].getName()) return true;
            }
            return false;
        };
        const changed = checkHandlesChanged();
        if (changed) {
            this.fireEvent("conditional-global-properties-changed", {
                globalProperties: newHandles,
            });
        }
    }

    private checkMandatoryHandlesChanged(
        previousState: GlobalPropertyState | undefined,
        newHandles: GlobalPropertyHandle[],
    ) {
        const changed: GlobalPropertyHandle[] = [];
        const completed: GlobalPropertyHandle[] = [];
        const remaining: GlobalPropertyHandle[] = [];
        const previousMandatory = calculateMandatoryStates(previousState);
        newHandles.forEach((handle) => {
            if (handle.isMandatory()) {
                const fulfilled = handle.isMandatoryFulfilled();
                if (fulfilled) {
                    completed.push(handle);
                } else {
                    remaining.push(handle);
                }
                const oldAspects = previousMandatory.get(handle.getName());
                if (oldAspects === undefined || oldAspects !== fulfilled) {
                    changed.push(handle);
                }
            }
        });
        this.fireEvent("global-properties-mandatory-changed", {
            changed,
            completed,
            remaining,
        });
    }

    private fireEvent<K extends keyof BundleEventMap>(event: K, data: BundleEventMap[K]): void {
        this.eventEmitter.emit(event, data);
    }

    getShareActions(): undefined | ShareAction[] {
        return this.shareActions;
    }

    getWorkflowViewerLink(): string {
        return this.workflowViewerLink;
    }

    getWorkflowViewerAmendLink(): string {
        return this.workflowViewerAmendLink;
    }

    async approve(note?: string, stakeholderId?: string): Promise<void> {
        await graphQlManager.getShadowGraphqlClient().mutate<{ bundleApprove: string }>({
            mutation: bundleApprovalQuery,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                id: this.id,
                note,
                stakeholderId,
            },
        });
    }

    async reject(note?: string, stakeholderId?: string): Promise<void> {
        await graphQlManager.getShadowGraphqlClient().mutate<{ bundleReject: string }>({
            mutation: bundleRejectionQuery,
            errorPolicy: "all",
            fetchPolicy: "no-cache",
            variables: {
                id: this.id,
                note,
                stakeholderId,
            },
        });
    }

    getQuoteCompleteMessage(): CompleteQuoteMessage {
        return {
            bundleId: this.id,
            bundleOwnerId: this.ownerId,
            quoteId: this.quoteId || "",
        };
    }
}
